import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  inject,
  output,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Patient } from '@core/models';
import { PatientService } from '@core/services';
import { NymblTranslatePipe, PersonsNamePipe } from '@shared/pipes';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { Skeleton } from 'primeng/skeleton';
import { ReplaySubject, debounceTime } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

/** A suggestion row in the topbar search: `#id  First Middle Last (MM-DD-YYYY)`. */
export interface PatientSearchSuggestion {
  id: number;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dob?: Date;
  active?: boolean;
  /** Shown in the input after selection, and what forceSelection matches against. */
  label: string;
}

/**
 * Topbar-only patient search.
 *
 * Deliberately separate from the shared `app-patient-autocomplete`: that one is a
 * ControlValueAccessor used inside forms, with active-only, client-sorted results.
 * The topbar is a navigation control that must also surface inactive patients (styled,
 * but still selectable) and trust the backend's system-setting-driven sort order.
 * Keeping them apart avoids growing the shared component with topbar-only toggles.
 */
@Component({
  selector: 'app-patient-search',
  imports: [
    AutoComplete,
    DatePipe,
    FormsModule,
    IconField,
    InputIcon,
    NymblTranslatePipe,
    Skeleton
  ],
  templateUrl: './patient-search.html',
  styleUrl: './patient-search.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Styles must reach PrimeNG's AutoComplete/IconField internals, which emulated
  // encapsulation cannot cross without ::ng-deep. Styles are unscoped here and
  // re-scoped manually via the `patient-search-host` host class.
  encapsulation: ViewEncapsulation.None,
  host: { class: 'patient-search-host' },
  providers: [PersonsNamePipe]
})
export class PatientSearch {
  private patientService = inject(PatientService);
  private personsNamePipe = inject(PersonsNamePipe);
  private datePipe = new DatePipe('en-US');

  readonly patientSelect = output<PatientSearchSuggestion>();

  protected readonly selected = signal<PatientSearchSuggestion | null>(null);
  protected readonly loading = signal(false);

  private searchSub = new ReplaySubject<string>(1);

  /**
   * No `inactive` param is sent, so the backend returns active and inactive patients in
   * its own sort order (driven by the "Patient Autocomplete Search Order By Name" system
   * setting) — there is no client-side re-sort here.
   */
  protected readonly suggestions = toSignal(
    this.searchSub.asObservable().pipe(
      tap(() => this.loading.set(true)),
      debounceTime(300),
      switchMap((term: string) => this.patientService.search(term)),
      map((patients) => (patients ?? []).map((p) => this.toSuggestion(p))),
      tap(() => this.loading.set(false))
    ),
    { initialValue: [] as PatientSearchSuggestion[] }
  );

  protected search(event: AutoCompleteCompleteEvent): void {
    this.searchSub.next(event.query);
  }

  protected onSelect(event: { value: PatientSearchSuggestion }): void {
    if (event?.value) this.patientSelect.emit(event.value);
  }

  private toSuggestion(patient: Patient): PatientSearchSuggestion {
    const name = this.personsNamePipe.transform(patient, 'FML');
    const dob = patient.dob
      ? this.datePipe.transform(patient.dob, 'MM-dd-yyyy')
      : null;
    return {
      id: patient.id,
      firstName: patient.firstName,
      middleName: patient.middleName,
      lastName: patient.lastName,
      dob: patient.dob,
      active: patient.active,
      label: dob ? `${name} (${dob})` : name
    };
  }
}
